// The kinds of blocks the editor offers in «/», «+» and «Превратить в» (decision 034, um/2026-10-30/03 п.2), with their markdown shortcuts.

import type { BlockKind } from '@confdeck/core-shared';

export interface KindDef { kind: BlockKind; label: string; icon: string; hint: string; keys: string; placeholder?: string }

export const KINDS: KindDef[] = [
  { kind: 'p', label: 'Текст', icon: 'paragraph', hint: 'Обычный абзац', keys: 'text paragraph абзац' },
  { kind: 'h1', label: 'Заголовок 1', icon: 'h1', hint: '#', keys: 'h1 heading заголовок', placeholder: 'Заголовок 1' },
  { kind: 'h2', label: 'Заголовок 2', icon: 'h2', hint: '##', keys: 'h2 heading заголовок', placeholder: 'Заголовок 2' },
  { kind: 'h3', label: 'Заголовок 3', icon: 'h3', hint: '###', keys: 'h3 heading заголовок', placeholder: 'Заголовок 3' },
  { kind: 'bullet', label: 'Маркированный список', icon: 'bullets', hint: '-', keys: 'list bullet ul список', placeholder: 'Список' },
  { kind: 'number', label: 'Нумерованный список', icon: 'numbers', hint: '1.', keys: 'list number ol список нумерованный', placeholder: 'Список' },
  { kind: 'todo', label: 'Чек-лист', icon: 'todo', hint: '[]', keys: 'todo check task чек задача', placeholder: 'Задача' },
  { kind: 'quote', label: 'Цитата', icon: 'quote', hint: '>', keys: 'quote цитата', placeholder: 'Цитата' },
  { kind: 'callout', label: 'Выноска', icon: 'callout', hint: 'Заметка на цветном фоне', keys: 'callout note выноска заметка важно', placeholder: 'Важное' },
  { kind: 'code', label: 'Код', icon: 'code', hint: '```', keys: 'code код' },
  { kind: 'divider', label: 'Разделитель', icon: 'divider', hint: '---', keys: 'divider hr line разделитель линия' },
  { kind: 'file', label: 'Картинка или файл', icon: 'image', hint: 'Загрузить с компьютера', keys: 'image file picture картинка файл изображение фото' },
  { kind: 'record', label: 'Ссылка на запись', icon: 'link', hint: 'Любая запись системы', keys: 'link record page ссылка запись' },
];

export const kindDef = (k: BlockKind) => KINDS.find((d) => d.kind === k)!;

/** Kinds whose content is typed text (the rest are a line, a file, a record). */
export const TEXT_KINDS = new Set<BlockKind>(['p', 'h1', 'h2', 'h3', 'bullet', 'number', 'todo', 'quote', 'callout', 'code']);
/** Kinds an Enter continues: the next item of a list. */
export const LIST_KINDS = new Set<BlockKind>(['bullet', 'number', 'todo']);

/** Markdown typed at the start of a paragraph turns it into a block: «# » a heading, «- » a list and so on. */
export function shortcutOf(text: string): { kind: BlockKind; length: number; checked?: boolean } | null {
  const m = /^(#{1,3} |[-*+] |\d+[.)] |\[ ?\] |\[x\] |> |```|---)/.exec(text);
  if (!m) return null;
  const s = m[1]!;
  if (s.startsWith('#')) return { kind: `h${s.length - 1}` as BlockKind, length: s.length };
  if (/^[-*+] $/.test(s)) return { kind: 'bullet', length: s.length };
  if (/^\d/.test(s)) return { kind: 'number', length: s.length };
  if (s.startsWith('[')) return { kind: 'todo', length: s.length, checked: s === '[x] ' };
  if (s === '> ') return { kind: 'quote', length: 2 };
  if (s === '```') return text === '```' ? { kind: 'code', length: 3 } : null;
  return text === '---' ? { kind: 'divider', length: 3 } : null;
}

/** Kinds found by what follows «/»: by the name or the English and Russian keywords. */
export function findKinds(q: string, allowed: ReadonlySet<BlockKind> | null = null): KindDef[] {
  const k = q.trim().toLowerCase();
  const all = allowed ? KINDS.filter((d) => allowed.has(d.kind)) : KINDS;
  return k ? all.filter((d) => d.label.toLowerCase().includes(k) || d.keys.split(' ').some((w) => w.startsWith(k))) : all;
}

/** A letter: text with headings, lists, quotes and a line; files go as attachments, links to records lead nowhere outside. */
export const LETTER_KINDS: BlockKind[] = ['p', 'h1', 'h2', 'h3', 'bullet', 'number', 'quote', 'callout', 'divider'];
/** A text block of a site page: its heading is a field of its own, so headings start at the second level. */
export const SITE_TEXT_KINDS: BlockKind[] = ['p', 'h2', 'h3', 'bullet', 'number', 'quote', 'callout'];
/** A note without a card to open links from (a cell, a comment of an action): all kinds but a file and a record link. */
export const NOTE_KINDS: BlockKind[] = ['p', 'h1', 'h2', 'h3', 'bullet', 'number', 'todo', 'quote', 'callout', 'code', 'divider'];
/** A short text in one place (a subtitle, a note): paragraphs with bold, italic and links only. */
export const LINE_KINDS: BlockKind[] = ['p'];
